// Accounting › Banking's To review (BankingPage.tsx): each bank line from the feed or a statement waits
// here until it is matched to what the books already hold (a deposit, a vendor payment), or categorised to
// an account (with a rule for next time). A RowList row: its description the name, the day and reference
// its line, the amount the strong end, and two actions at rest, Find a match and Categorise. Either opens
// under the line, in place, as a part of the row: the suggestion (Accept match, or Something else), or the
// form (the account, a memo, the recoverable tax on money out, a rule). The server holds back a line that
// looks like something already in the books (possible_duplicate): its words say which, and the person can
// still say it's different.
import {
  Button,
  Checkbox,
  Link,
  Notice,
  RowList,
  SelectField,
  TextField,
  type Row,
} from '@flowology/ui-web/fl';
import { RpcError } from '@flowology/client';
import { useState } from 'react';
import { client } from '../api.ts';
import { docHref } from '../shared/doc-link.ts';
import { dayText, money, parseMoney } from './model.ts';
import type { Account, BankLine, Suggestions } from './types.ts';
import { errorText, type Act } from './ui.tsx';

/** A line opened under itself: what was suggested for it, or its categorise form. */
type Opened = { mode: 'suggest'; sugg: Suggestions } | { mode: 'categorise' };

function Suggestion({
  line,
  sugg,
  act,
  onElse,
}: {
  line: BankLine;
  sugg: Suggestions;
  act: Act;
  onElse: () => void;
}) {
  const best = sugg.matches[0];
  if (!best)
    return (
      <div className="bp-bank-part">
        <p className="bp-bank-say">No match found. Nothing open in the books looks like this line.</p>
        <div className="bp-bank-acts">
          <Link onPress={onElse}>Categorise</Link>
        </div>
      </div>
    );
  return (
    <div className="bp-bank-part">
      <p className="bp-bank-line">
        <b>Suggested match:</b> {best.why} (
        <Link href={docHref({ kind: 'journal-entry', id: best.entryId })}>{best.entryNumber}</Link>)
      </p>
      <div className="bp-bank-acts">
        <Button
          isDisabled={act.busy}
          onPress={() =>
            void act.run(
              'bank.match',
              { bankTransactionId: line.id, journalLineIds: best.journalLineIds },
              'Matched.',
            )
          }
        >
          Accept match
        </Button>
        <Link onPress={onElse}>Something else</Link>
      </div>
    </div>
  );
}

function Categorise({
  line,
  accounts,
  taxCode,
  act,
  toast,
  reload,
  onCancel,
  onLook,
}: {
  line: BankLine;
  accounts: Account[];
  taxCode: string;
  act: Act;
  toast: (m: string) => void;
  reload: () => Promise<unknown>;
  onCancel: () => void;
  /** Find a match instead (the duplicate's way out). */
  onLook: () => void;
}) {
  const [accountId, setAccountId] = useState('');
  const [memo, setMemo] = useState('');
  const [tax, setTax] = useState('');
  const [rule, setRule] = useState(false);
  const [dupe, setDupe] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const out = line.amountCents < 0;
  const categorise = async (notDuplicate = false) => {
    if (!accountId) return toast('Pick the account it belongs to.');
    const taxCents = out && tax.trim() ? parseMoney(tax) : 0;
    if (taxCents === null || taxCents < 0) return toast(`Type the ${taxCode} in it, like 20.32.`);
    setPosting(true);
    let ok = false;
    try {
      await client.call('bank.categorise', {
        bankTransactionId: line.id,
        accountId,
        ...(memo ? { memo } : {}),
        ...(taxCents ? { taxes: [{ code: taxCode, taxCents }] } : {}),
        ...(notDuplicate ? { notDuplicate: true } : {}),
      });
      ok = true;
      setDupe(null);
      toast('Categorised and posted.');
      await reload();
    } catch (e) {
      const reason =
        e instanceof RpcError ? (e.detail as { reason?: string } | undefined)?.reason : undefined;
      if (reason === 'possible_duplicate') setDupe(errorText(e));
      else toast(errorText(e));
    } finally {
      setPosting(false);
    }
    if (ok && rule)
      await act.run(
        'bank.createRule',
        { contains: line.description, direction: out ? 'out' : 'in', accountId },
        'Rule saved: lines like this are categorised the same way.',
      );
  };
  return (
    <div className="bp-bank-part">
      <div className="bp-bank-form">
        <SelectField
          label="Account"
          value={accountId}
          onChange={setAccountId}
          options={[
            { value: '', label: 'Choose…' },
            ...accounts.map((a) => ({ value: a.id, label: `${a.code} ${a.name}` })),
          ]}
        />
        <TextField label="Memo" value={memo} onChange={setMemo} placeholder="Optional" />
        {out && (
          <TextField
            label={`${taxCode} in it (recoverable)`}
            inputMode="decimal"
            value={tax}
            onChange={setTax}
            placeholder="0.00"
          />
        )}
        <div className="bp-bank-wide">
          <Checkbox isSelected={rule} onChange={setRule}>
            Make a rule for lines like “{line.description}”
          </Checkbox>
        </div>
      </div>
      {dupe && (
        <div role="alert">
          <Notice
            title={dupe}
            actions={
              <>
                <Button
                  isDisabled={posting}
                  onPress={() => {
                    setDupe(null);
                    onLook();
                  }}
                >
                  Find a match
                </Button>
                <Link isDisabled={posting} onPress={() => void categorise(true)}>
                  It’s different, categorise it
                </Link>
              </>
            }
          />
        </div>
      )}
      <div className="bp-bank-acts">
        <Button variant="primary" isDisabled={act.busy || posting} onPress={() => void categorise()}>
          Categorise
        </Button>
        <Link onPress={onCancel}>Cancel</Link>
      </div>
    </div>
  );
}

export function BankingReview({
  lines,
  accounts,
  taxCode,
  act,
  toast,
  reload,
}: {
  lines: readonly BankLine[];
  accounts: Account[];
  /** The recoverable tax a purchase carries here (GST or HST), split out of money out (QA-810). */
  taxCode: string;
  act: Act;
  toast: (m: string) => void;
  reload: () => Promise<unknown>;
}) {
  const [opened, setOpened] = useState<Record<string, Opened>>({});
  const open = (id: string, o: Opened | null) =>
    setOpened((m) => {
      const rest = Object.fromEntries(Object.entries(m).filter(([k]) => k !== id));
      return o ? { ...rest, [id]: o } : rest;
    });
  const look = async (l: BankLine) => {
    try {
      const sugg = await client.call<Suggestions>('bank.suggestMatches', { bankTransactionId: l.id });
      open(l.id, { mode: 'suggest', sugg });
    } catch (e) {
      toast(errorText(e));
    }
  };
  const rows: Row[] = lines.map((l) => {
    const o = opened[l.id];
    const part = !o ? null : o.mode === 'suggest' ? (
      <Suggestion line={l} sugg={o.sugg} act={act} onElse={() => open(l.id, { mode: 'categorise' })} />
    ) : (
      <Categorise
        line={l}
        accounts={accounts}
        taxCode={taxCode}
        act={act}
        toast={toast}
        reload={reload}
        onCancel={() => open(l.id, null)}
        onLook={() => void look(l)}
      />
    );
    return {
      id: l.id,
      name: l.description,
      label: l.description,
      lines: [
        { text: `${dayText(l.postedOn)}${l.reference ? ` · ${l.reference}` : ''}` },
        ...(part ? [{ part }] : []),
      ],
      end: { figure: money(l.amountCents, { signed: true }) },
      actions: o
        ? []
        : [
            {
              id: 'match',
              text: 'Find a match',
              label: `Find a match for ${l.description}`,
              onPress: () => void look(l),
            },
            {
              id: 'categorise',
              text: 'Categorise',
              label: `Categorise ${l.description}`,
              onPress: () => open(l.id, { mode: 'categorise' }),
            },
          ],
      atRest: 2,
    };
  });
  return <RowList label="To review" rows={rows} />;
}
